import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import { cond, equals, always, anyPass, T } from 'ramda';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';

import DelinquencyServiceV2 from '../../services/delinquencyServiceV2';
import ActivityTypeService from '../../services/activityTypeService';
import ActivityService from '../../services/activityService';
import { createActivity } from '../../components/CreateActivityModal';

import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import {
  useGetShowOptionalColumns,
  useGetFilterOptions,
  useHeaders,
  useRows,
  useCSVPDFExport,
  useGetFooterRow,
} from '../ManageDelinquency/hooksV2';
import useProspectAssignees from '../../hooks/data-fetching/useProspectAssignees';
import useActivityTypes from '../../hooks/data-fetching/useActivityTypes';

import { PROPERTY_PATH_MAP } from '../ManageDelinquency/constantsv2';
import { normalizeDelinquencyRows } from '../ManageDelinquency/utilsV2';
import peopleProfileMessages from '../PeopleProfile/messages';
import useAbortController from '../../hooks/useAbortController';

export const useCentralizedDelinquentAndPrepaid = ({
  organizationId,
  NAME,
  intl,
  locale,
  store,
}) => {
  const name = useUniqueTableName(NAME);
  const { results, isLoading, refreshTable } =
    useFetchCentralizedDelinquentAndPrepaid({
      organizationId,
    });

  const { showCAMOnlyBalances, showSubsidyOnlyBalances } =
    useGetShowOptionalColumns(results, { centralized: true });

  const { prospectAssignees: users } = useProspectAssignees();

  const assigneeOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        text: `${u.firstName} ${u.lastName}`,
      })),
    [users],
  );

  const { activityTypes } = useActivityTypes();
  const { activityTypesList } = useMemo(() => {
    const activityTypeService = new ActivityTypeService();
    const activityTypesList = activityTypeService.generateActivityTypesList({
      activityTypes,
      locale,
      intl,
    });

    return { activityTypeService, activityTypesList };
  }, [activityTypes, intl, locale]);

  const {
    statusOptions,
    floorPlanOptions,
    buildingNumberOptions,
    propertyNameOptions,
  } = useGetFilterOptions({
    results,
  });

  const headers = useHeaders({
    statusOptions,
    floorPlanOptions,
    buildingNumberOptions,
    showSubsidyOnlyBalances,
    showCAMOnlyBalances,
    centralizedDelinquency: true,
    propertyNameOptions,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'propertyName',
    initialOrder: 'ASC',
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const handleCreateActivity = async ({
    intl,
    organizationId,
    refreshTable,
    newActivity,
  }): Promise<void> => {
    try {
      const activityService = new ActivityService();

      await activityService.save(newActivity, organizationId);

      toastr.success(
        intl.formatMessage(peopleProfileMessages.successHeader),
        intl.formatMessage(
          peopleProfileMessages.successActivityCreateDescription,
        ),
      );

      refreshTable();
    } catch (err) {
      toastr.error(
        intl.formatMessage(peopleProfileMessages.errorHeader),
        err.toString(),
      );
    }
  };

  const handleCreateActivityClick = useCallback(
    async (row) => {
      const delinquency = row;
      const prospectInfo = {
        id: delinquency.prospectId,
        firstName: delinquency.firstName,
        lastName: delinquency.lastName,
        createdAt: delinquency.prospectCreatedAt,
      };

      const getStageProps = cond([
        [
          equals('Applicant'),
          always({ stage: 'applicant', urlId: delinquency.applicationId }),
        ],
        [
          anyPass([equals('Current Resident'), equals('Prior Resident')]),
          always({ stage: 'resident', urlId: delinquency.residentId }),
        ],
        [
          anyPass([equals('Prospect'), T]),
          always({ stage: 'prospect', urlId: delinquency.prospectId }),
        ],
      ]);
      const stageProps = getStageProps(delinquency.customerStatus);

      const newActivity = await createActivity({
        intl,
        activityTypesList,
        store,
        prospect: prospectInfo,
        assigneeList: assigneeOptions,
        showViewProfileBtn: true,
        propertyId: row.propertyId,
        isCentralizedRows: true,
        ...stageProps,
      });

      await handleCreateActivity({
        intl,
        organizationId,
        refreshTable,
        newActivity: {
          ...newActivity,
          customerStatus: delinquency.customerStatus ?? 'Prospect',
        },
      });
    },
    [
      intl,
      activityTypesList,
      assigneeOptions,
      store,
      organizationId,
      refreshTable,
    ],
  );

  const rows = useRows({
    sortedAndFilteredResults,
    handleCreateActivityClick,
    isCentralizedRows: true,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const footerRow = useGetFooterRow({
    filteredHeaders,
    sortedAndFilteredResults,
  });

  const { onCSVButtonClick } = useCSVPDFExport({
    propertyName: name,
    hasAnyFilters:
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length,
    filteredHeaders,
    rows,
    footerRow,
    results: sortedAndFilteredResults,
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    footerRow,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    onCSVButtonClick,
    totalCount: results?.length ?? 0,
  };
};

export const useFetchCentralizedDelinquentAndPrepaid = ({ organizationId }) => {
  const delinquencyService = useMemo(() => new DelinquencyServiceV2(), []);
  const queryKey = [organizationId];
  const options = useAbortController();
  const {
    data,
    isLoading,
    refetch: refreshTable,
  } = useQuery(
    ['CentralizedDelinquentAndPrepaid', queryKey],
    () =>
      delinquencyService.getCentralizedDelinquencies({
        organizationId,
        options,
      }),
    { enabled: !!organizationId },
  );

  const results = useMemo(() => normalizeDelinquencyRows(data ?? []), [data]);
  return {
    results,
    isLoading,
    refreshTable,
  };
};
